import React, { useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { getCanonicalVehicleStatus } from "../../utils/vehicleStatus";
import { useGetAllLiteDevicesQuery } from "../../redux/admin/adminApi";

const safeLower = (value) => String(value || "").toLowerCase();

export default function GeofenceAssignModal({
  open,
  geofence,
  devices = [],
  onClose,
  onToggleDevice,
  pendingDeviceIds = {},
}) {
  const [search, setSearch] = useState("");
  const { data: fallbackDevices = [] } = useGetAllLiteDevicesQuery(undefined, {
    skip: !open,
  });
  const sourceDevices = useMemo(() => {
    if (Array.isArray(devices) && devices.length) return devices;
    if (Array.isArray(devices?.data) && devices.data.length) return devices.data;
    if (Array.isArray(fallbackDevices)) return fallbackDevices;
    return [];
  }, [devices, fallbackDevices]);

  const rows = useMemo(() => {
    const q = safeLower(search).trim();
    const assignedIds = new Set(
      (Array.isArray(geofence?.assignedDeviceIds) ? geofence.assignedDeviceIds : []).map((value) => String(value))
    );

    return sourceDevices
      .filter((device) => {
        if (!q) return true;
        return (
          safeLower(device?.name).includes(q) ||
          safeLower(device?.imei).includes(q) ||
          safeLower(device?.msdn).includes(q) ||
          safeLower(device?.model).includes(q)
        );
      })
      .map((device) => ({
        ...device,
        checked: assignedIds.has(String(device?._id || "")),
        status: getCanonicalVehicleStatus(device),
      }));
  }, [geofence, search, sourceDevices]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} fullWidth>
      <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        Assign Devices
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 1, pb: 2.5 }}>
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search"
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "54px 1.1fr 1.2fr 1fr 1.2fr 1.1fr 1fr",
            gap: 1,
            px: 1,
            py: 1,
            borderBottom: "1px solid #e5e7eb",
            color: "text.secondary",
            fontSize: "12px",
            fontWeight: 700,
          }}
        >
          <Box />
          <Typography sx={{ fontSize: "12px", fontWeight: 700 }}>Vehicle No</Typography>
          <Typography sx={{ fontSize: "12px", fontWeight: 700 }}>IMEI</Typography>
          <Typography sx={{ fontSize: "12px", fontWeight: 700 }}>Status</Typography>
          <Typography sx={{ fontSize: "12px", fontWeight: 700 }}>Last Update</Typography>
          <Typography sx={{ fontSize: "12px", fontWeight: 700 }}>Sim No</Typography>
          <Typography sx={{ fontSize: "12px", fontWeight: 700 }}>Model</Typography>
        </Box>
        <Box sx={{ maxHeight: 560, overflowY: "auto" }}>
          {rows.map((device) => {
            const deviceId = String(device?._id || "");
            const pending = pendingDeviceIds?.[deviceId] === true;
            return (
              <Box
                key={deviceId}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "54px 1.1fr 1.2fr 1fr 1.2fr 1.1fr 1fr",
                  gap: 1,
                  px: 1,
                  py: 0.75,
                  borderBottom: "1px solid #f1f5f9",
                  alignItems: "center",
                }}
              >
                <Checkbox
                  checked={device.checked}
                  disabled={pending}
                  onChange={(event) => onToggleDevice?.(device, event.target.checked)}
                />
                <Typography sx={{ fontSize: "12px" }}>{device?.vehicleNumber || device?.name || "-"}</Typography>
                <Typography sx={{ fontSize: "12px" }}>{device?.imei || "-"}</Typography>
                <Typography sx={{ fontSize: "12px", textTransform: "capitalize" }}>{String(device.status || "-").replaceAll("_", " ")}</Typography>
                <Typography sx={{ fontSize: "12px" }}>{device?.lastDataReceivedAt ? new Date(device.lastDataReceivedAt).toLocaleString() : "-"}</Typography>
                <Typography sx={{ fontSize: "12px" }}>{device?.msdn || "-"}</Typography>
                <Typography sx={{ fontSize: "12px" }}>{device?.model || "-"}</Typography>
              </Box>
            );
          })}
          {!rows.length ? (
            <Box sx={{ p: 3, color: "text.secondary", textAlign: "center" }}>
              No devices found.
            </Box>
          ) : null}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
